import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
} from '@openzeppelin/ui-components';
import { cn } from '@openzeppelin/ui-utils';
import { Icon } from '@ascend/shared/components';
import {
  DataTable,
  type DataTableColumn,
  PaginationControls,
  TABLE_PAGE_SIZE_OPTIONS,
  type SortDirection,
} from '../components/ListControls';
import {
  CLIENT_LENDING_POOLS,
  getPoolDetail,
  type ClientIOU,
  type ClientIOUEvent,
  type ClientYieldEntry,
} from '../data/mockAdapter';

// ─── Types ──────────────────────────────────────────────────────────────────────

type YieldSortKey = 'week' | 'yield' | 'cumulative';

// ─── Helpers ────────────────────────────────────────────────────────────────────

function getWithdrawableColor(pct: number): string {
  if (pct >= 100) return 'text-ascend-success';
  if (pct >= 50) return 'text-amber-600';
  return 'text-ascend-error';
}

const IOU_STATUS_STYLES: Record<string, string> = {
  outstanding: 'bg-amber-100 text-amber-700',
  partially_repaid: 'bg-blue-100 text-blue-700',
  settled: 'bg-green-100 text-green-700',
};

const IOU_STATUS_LABELS: Record<string, string> = {
  outstanding: 'Outstanding',
  partially_repaid: 'Partially Repaid',
  settled: 'Settled',
};

// ─── Page ───────────────────────────────────────────────────────────────────────

export function LenderPositionDetailPage() {
  const { poolId } = useParams<{ poolId: string }>();
  const navigate = useNavigate();

  const pool = CLIENT_LENDING_POOLS.find((p) => p.id === poolId) ?? null;

  const [detail, setDetail] = useState<{
    ious: ClientIOU[];
    iouEvents: ClientIOUEvent[];
    yieldHistory: ClientYieldEntry[];
    tvlUsd: number;
    utilizationPct: number;
  } | null>(null);

  useEffect(() => {
    if (!poolId) return;
    getPoolDetail(poolId).then((d) => setDetail(d));
  }, [poolId]);

  if (!pool) {
    return (
      <div className="space-y-6">
        <Link
          to="/lender/dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-4 w-4" />
          Back to Lender Dashboard
        </Link>
        <div className="rounded-xl border border-dashed border-border p-12 text-center">
          <p className="text-sm text-muted-foreground">Position not found.</p>
        </div>
      </div>
    );
  }

  const lpShare = detail && detail.tvlUsd > 0
    ? ((pool.currentValueUsd / detail.tvlUsd) * 100).toFixed(2)
    : '0';
  const lpToUnderlyingRatio = pool.lpTokensRaw > 0
    ? (pool.currentValueUsd / pool.lpTokensRaw).toFixed(4)
    : '1.0000';
  const projectedAnnual = Math.round(pool.depositedUsd * pool.apyPct / 100);
  const withdrawableAmount = Math.round(pool.currentValueUsd * pool.withdrawablePct / 100);
  const totalActiveIou = detail
    ? detail.ious.filter((i) => i.status !== 'settled').reduce((s, i) => s + i.remainingAmountUsd, 0)
    : 0;

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Back link + pool name */}
      <div className="flex items-center justify-between">
        <Link
          to="/lender/dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-4 w-4" />
          Back to Lender Dashboard
        </Link>
        <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
          {pool.apy}
        </span>
      </div>

      <div>
        <h1 className="text-xl sm:text-2xl font-bold">{pool.name}</h1>
        <p className="text-sm text-muted-foreground mt-1">Position details and IOU exposure</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        {/* Left column */}
        <div className="space-y-6">
          {/* Your Deposit */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Your Deposit</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg bg-muted/50 p-4">
                  <div className="text-xs text-muted-foreground">Deposited</div>
                  <div className="text-2xl font-bold mt-1">{pool.deposited}</div>
                </div>
                <div className="rounded-lg bg-muted/50 p-4">
                  <div className="text-xs text-muted-foreground">LP-Tokens</div>
                  <div className="text-2xl font-bold mt-1">{pool.lpTokens}</div>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">LP-Token Share</span>
                  <span className="font-medium">{lpShare}% of pool</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">LP to Underlying Ratio</span>
                  <span className="font-medium">{lpToUnderlyingRatio}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Current Value */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Current Value</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Principal</span>
                  <span className="font-medium">{pool.deposited}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Earned Yield</span>
                  <span className="font-medium text-ascend-success">{pool.earnedYield}</span>
                </div>
                <div className="flex justify-between border-t border-border pt-3">
                  <span className="font-semibold">Total</span>
                  <span className="text-2xl font-bold">{pool.currentValue}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Active IOUs */}
          {detail && detail.ious.length > 0 && (
            <Card>
              <CardHeader className="p-6">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Icon name="streamline:interface-file-text-text-common-file" className="h-5 w-5" />
                  Active IOUs
                </CardTitle>
                <CardDescription>IOU tokens from DDF partial liquidations affecting this pool.</CardDescription>
              </CardHeader>
              <CardContent className="p-6 pt-0">
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Facility</th>
                        <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">Original</th>
                        <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">Repaid</th>
                        <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">Remaining</th>
                        <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {detail.ious.map((iou) => (
                        <tr key={iou.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-2.5 font-medium">{iou.facilityName}</td>
                          <td className="px-4 py-2.5 text-right">{iou.originalAmount}</td>
                          <td className="px-4 py-2.5 text-right text-ascend-success">{iou.repaidAmount}</td>
                          <td className="px-4 py-2.5 text-right font-semibold">{iou.remainingAmount}</td>
                          <td className="px-4 py-2.5 text-right">
                            <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-medium', IOU_STATUS_STYLES[iou.status])}>
                              {IOU_STATUS_LABELS[iou.status]}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* IOU Activity */}
          {detail && detail.iouEvents.length > 0 && (
            <Card>
              <CardHeader className="p-6">
                <CardTitle className="text-base">IOU Activity</CardTitle>
                <CardDescription>Mint and burn events for IOU tokens in this pool.</CardDescription>
              </CardHeader>
              <CardContent className="p-6 pt-0">
                <div className="space-y-3">
                  {detail.iouEvents.map((ev) => (
                    <div key={ev.id} className="flex items-center justify-between rounded-lg border border-border p-3">
                      <div className="flex items-center gap-3">
                        <span className={cn(
                          'rounded-full px-2.5 py-0.5 text-xs font-medium',
                          ev.eventType === 'mint' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700',
                        )}>
                          {ev.eventType === 'mint' ? 'Mint' : 'Burn'}
                        </span>
                        <span className="text-sm text-muted-foreground">{ev.createdAt}</span>
                      </div>
                      <span className={cn(
                        'text-sm font-semibold',
                        ev.eventType === 'mint' ? 'text-blue-700' : 'text-ascend-success',
                      )}>
                        {ev.eventType === 'mint' ? '+' : '-'}{ev.amount}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Yield Breakdown */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="flex items-center gap-2 text-base">
                <Icon name="streamline:graph-bar-increase" className="h-5 w-5" />
                Yield Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="grid grid-cols-3 gap-4">
                <div className="rounded-lg bg-muted/50 p-4 text-center">
                  <div className="text-xs text-muted-foreground">APY</div>
                  <div className="text-2xl font-bold text-ascend-success mt-1">{pool.apy}</div>
                </div>
                <div className="rounded-lg bg-muted/50 p-4 text-center">
                  <div className="text-xs text-muted-foreground">Earned to Date</div>
                  <div className="text-2xl font-bold mt-1">{pool.earnedYield}</div>
                </div>
                <div className="rounded-lg bg-muted/50 p-4 text-center">
                  <div className="text-xs text-muted-foreground">Projected Annual</div>
                  <div className="text-2xl font-bold mt-1">${projectedAnnual.toLocaleString()}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Yield History */}
          {detail && detail.yieldHistory.length > 0 && (
            <YieldHistoryCard yieldHistory={detail.yieldHistory} />
          )}
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Pool Info */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Pool Info</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Pool</span>
                <span className="font-medium">{pool.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">APY</span>
                <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                  {pool.apy}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">TVL</span>
                <span className="font-medium">{pool.tvl}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Utilization</span>
                <span className="font-medium">{pool.utilization}</span>
              </div>
            </CardContent>
          </Card>

          {/* Withdrawable */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Withdrawable</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Available</span>
                <span className={cn('text-2xl font-bold', getWithdrawableColor(pool.withdrawablePct))}>
                  {pool.withdrawable}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-medium">${withdrawableAmount.toLocaleString()}</span>
              </div>
              {pool.withdrawablePct < 100 && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <Icon name="streamline:warning-triangle" className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800">
                    Withdrawal restricted due to active IOU exposure.
                    {totalActiveIou > 0 && ` $${totalActiveIou.toLocaleString()} in outstanding IOUs.`}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Actions</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-2">
              <Button className="w-full" onClick={() => navigate(`/lender/deposit?poolId=${pool.id}`)}>
                <Icon name="streamline:interface-add-1-expand-cross-buttons-button-more-remove-plus-add" className="mr-2 h-4 w-4" />
                Deposit
              </Button>
              <Button variant="outline" className="w-full" onClick={() => navigate(`/lender/withdraw?poolId=${pool.id}`)}>
                <Icon name="streamline:interface-file-remove-file-common-remove-minus-subtract" className="mr-2 h-4 w-4" />
                Withdraw
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ─── Yield History Sub-component ─────────────────────────────────────────────

function YieldHistoryCard({ yieldHistory }: { yieldHistory: ClientYieldEntry[] }) {
  const [sortKey, setSortKey] = useState<YieldSortKey>('week');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(TABLE_PAGE_SIZE_OPTIONS[0]);

  // Reset to page 1 when sort changes.
  const sortKeyId = `${sortKey}|${sortDirection}`;
  const [prevSortKeyId, setPrevSortKeyId] = useState(sortKeyId);
  if (prevSortKeyId !== sortKeyId) {
    setPrevSortKeyId(sortKeyId);
    setPage(1);
  }

  const sorted = useMemo(() => {
    const items = [...yieldHistory];
    items.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'week': cmp = a.order - b.order; break;
        case 'yield': cmp = a.yieldUsd - b.yieldUsd; break;
        case 'cumulative': cmp = a.cumulativeUsd - b.cumulativeUsd; break;
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return items;
  }, [yieldHistory, sortKey, sortDirection]);

  const paginated = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
    const safe = Math.min(page, totalPages);
    return sorted.slice((safe - 1) * pageSize, safe * pageSize);
  }, [sorted, page, pageSize]);

  function onSort(nextKey: YieldSortKey) {
    setSortDirection((prev) => (sortKey === nextKey ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'));
    setSortKey(nextKey);
  }

  const columns = useMemo<DataTableColumn<ClientYieldEntry, YieldSortKey>[]>(
    () => [
      {
        id: 'week',
        label: 'Week',
        sortable: true,
        sortKey: 'week',
        renderCell: (row) => <span className="text-muted-foreground">{row.week}</span>,
      },
      {
        id: 'yield',
        label: 'Yield',
        align: 'right',
        sortable: true,
        sortKey: 'yield',
        renderCell: (row) => <span className="text-ascend-success font-medium">{row.yield}</span>,
      },
      {
        id: 'cumulative',
        label: 'Cumulative',
        align: 'right',
        sortable: true,
        sortKey: 'cumulative',
        renderCell: (row) => <span className="font-semibold">{row.cumulative}</span>,
      },
    ],
    [],
  );

  return (
    <Card>
      <CardHeader className="p-6">
        <CardTitle className="text-base">Yield History</CardTitle>
        <CardDescription>Weekly yield accrual for this pool.</CardDescription>
      </CardHeader>
      <CardContent className="p-6 pt-0">
        <div className="overflow-x-auto">
          <DataTable<ClientYieldEntry, YieldSortKey>
            columns={columns}
            rows={paginated}
            rowKey={(row) => row.id}
            sort={{ key: sortKey, direction: sortDirection, onSort }}
            emptyMessage="No yield history available."
          />
        </div>
        <PaginationControls
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalItems={yieldHistory.length}
          label="weeks"
        />
      </CardContent>
    </Card>
  );
}
